import { Editor, Frame } from "@craftjs/core";
import { useEffect, useMemo, useState } from "react";
import { craftResolver } from "./resolver";
import { parseCraftState, createEmptyCraftState, type CraftNodeMap } from "@/lib/craft/serialize";
import { getSiteSettingsPublic } from "@/lib/api/public.functions";
import type { PublicSiteSettings } from "@/lib/api/public.functions";
import { buildResponsiveCss, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { filterKnownCraftBlocks } from "@/lib/craft/craft-state-utils";

type CraftRendererProps = {
  craftState: string | object | null | undefined;
  craftCustomCss?: string | null;
  globalCss?: string | null;
  className?: string;
};

function collectResponsiveCss(state: CraftNodeMap): string {
  const chunks: string[] = [];
  for (const [id, node] of Object.entries(state)) {
    if (id === "ROOT") continue;
    const style = node.props?.style as ResponsiveStyleProps | undefined;
    if (style?.desktop || style?.tablet || style?.mobile) {
      chunks.push(buildResponsiveCss(id, style));
    }
  }
  return chunks.filter(Boolean).join("\n");
}

export function CraftRenderer({
  craftState,
  craftCustomCss,
  globalCss: globalCssProp,
  className,
}: CraftRendererProps) {
  const [globalCss, setGlobalCss] = useState(globalCssProp ?? "");

  useEffect(() => {
    if (globalCssProp !== undefined) return;
    getSiteSettingsPublic()
      .then((s: PublicSiteSettings) => setGlobalCss(s.craftGlobalCss ?? ""))
      .catch(() => {});
  }, [globalCssProp]);

  const raw = parseCraftState(craftState) ?? createEmptyCraftState();
  const known = new Set(Object.keys(craftResolver));
  const data = filterKnownCraftBlocks(raw as Record<string, unknown>, known) as CraftNodeMap;
  const responsiveCss = useMemo(() => collectResponsiveCss(data), [data]);
  const css = [globalCss, responsiveCss, craftCustomCss].filter(Boolean).join("\n");

  return (
    <div className={className}>
      {css ? <style dangerouslySetInnerHTML={{ __html: css }} /> : null}
      <Editor enabled={false} resolver={craftResolver}>
        <Frame data={data} />
      </Editor>
    </div>
  );
}
